import React, { useState, useRef, useEffect } from "react";
import api from "@/lib/apiClient";
import { Sparkles, X, Send } from "lucide-react";

export default function AIAssistant() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hey! I'm your CommunityPump AI coach. Ask me about workouts, nutrition, substitutions or mindfulness." },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const q = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setLoading(true);
    try {
      const res = await api.post("/ai/assistant", { message: q });
      setMessages((m) => [...m, { role: "assistant", text: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I couldn't respond right now." }]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-assistant-fab"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#FF6B6B] flex items-center justify-center hover:brightness-110 transition-all animate-pulse-glow"
      >
        {open ? <X className="w-6 h-6 text-black" /> : <Sparkles className="w-6 h-6 text-black" />}
      </button>

      {open && (
        <div data-testid="ai-assistant-panel" className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[70vh] max-h-[520px] bg-[#1E1E1E] border border-[#2A2A2A] rounded-2xl flex flex-col overflow-hidden shadow-2xl">
          <div className="px-4 py-3 border-b border-[#2A2A2A] flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#4ECCD3]/15 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#4ECCD3]" />
            </div>
            <div>
              <p className="font-semibold text-white text-sm">AI Coach</p>
              <p className="text-[10px] text-[#A0A0A0] uppercase tracking-wider">Powered by Gemini</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${
                  m.role === "user" ? "bg-[#FF6B6B] text-black" : "bg-[#2A2A2A] text-white"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-[#2A2A2A] rounded-2xl px-3 py-2 text-sm text-[#A0A0A0]">Thinking…</div>
              </div>
            )}
            <div ref={endRef} />
          </div>

          <div className="p-3 border-t border-[#2A2A2A] flex gap-2">
            <input
              data-testid="ai-assistant-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask your AI coach…"
              className="flex-1 bg-[#121212] border border-[#2A2A2A] rounded-full px-4 py-2 text-sm text-white placeholder-[#A0A0A0] focus:outline-none focus:ring-1 focus:ring-[#FF6B6B]"
            />
            <button
              data-testid="ai-assistant-send-btn"
              onClick={send}
              className="w-9 h-9 rounded-full bg-[#FF6B6B] flex items-center justify-center hover:brightness-110 transition-all shrink-0"
            >
              <Send className="w-4 h-4 text-black" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
